import axios from "axios";

const ANDROID_EMULATOR_API_URL = "https://resume-maker-lc.onrender.com/api";
const DEFAULT_NETWORK_API_URL = "https://resume-maker-lc.onrender.com/api";
const EXPLICIT_API_URL = process.env.REACT_APP_API_URL;
const LOCAL_NETWORK_API_URL = process.env.REACT_APP_LOCAL_API_URL;

const getDefaultLocalApiUrl = () => {
  const hostname = window.location.hostname;
  const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";
  return isLocalHost ? "http://localhost:5000/api" : "https://resume-maker-lc.onrender.com/api";
};

const normalizeApiUrl = (url) => {
  const trimmed = String(url || "")
    .replace("https://resume-maker-lc.onrender.com", "https://resume-maker-lc.onrender.com")
    .replace(/\/$/, "");
  return trimmed.endsWith("/api") ? trimmed : `${trimmed}/api`;
};

const configuredApiUrl = normalizeApiUrl(
  EXPLICIT_API_URL ||
  getDefaultLocalApiUrl()
);

const uniqueUrls = (urls) => [...new Set(urls.filter(Boolean).map(normalizeApiUrl))];

const isNativeApp = () => Boolean(window.Capacitor?.isNativePlatform?.());

const getStoredActiveApiUrl = () => {
  const activeApiUrl = localStorage.getItem("activeApiUrl");
  if (!activeApiUrl) return null;

  const normalizedActiveUrl = normalizeApiUrl(activeApiUrl);
  if (normalizedActiveUrl.includes("ai-learn-hub.onrender.com")) {
    localStorage.removeItem("activeApiUrl");
    return null;
  }

  return normalizedActiveUrl;
};

const getApiCandidates = () => {
  const activeApiUrl = getStoredActiveApiUrl();

  if (isNativeApp()) {
    return uniqueUrls([
      EXPLICIT_API_URL,
      LOCAL_NETWORK_API_URL,
      DEFAULT_NETWORK_API_URL,
      ANDROID_EMULATOR_API_URL,
      activeApiUrl
    ]);
  }

  return uniqueUrls([
    activeApiUrl,
    configuredApiUrl,
    LOCAL_NETWORK_API_URL,
    DEFAULT_NETWORK_API_URL,
    getDefaultLocalApiUrl()
  ]);
};

export const API_URL = configuredApiUrl;

const API_ROOT = API_URL.replace(/\/api\/?$/, "");

axios.defaults.baseURL = API_ROOT;

export const getActiveApiUrl = () => getStoredActiveApiUrl() || API_URL;

export const apiFetch = async (path, options = {}, timeoutMs = 15000) => {
  const endpoint = path.startsWith("/") ? path : `/${path}`;
  const candidates = getApiCandidates();
  let lastError = null;

  for (const baseUrl of candidates) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(`${baseUrl}${endpoint}`, {
        ...options,
        signal: controller.signal
      });

      const contentType = response.headers.get("content-type") || "";
      if (response.status === 404 && contentType.includes("text/html")) {
        if (localStorage.getItem("activeApiUrl") === baseUrl) {
          localStorage.removeItem("activeApiUrl");
        }
        lastError = new Error(`Route not found at ${baseUrl}${endpoint}`);
        continue;
      }

      localStorage.setItem("activeApiUrl", baseUrl);
      return response;
    } catch (error) {
      lastError = error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  if (lastError?.name === "AbortError") {
    throw new Error(`Request timed out. Tried: ${candidates.join(", ")}`);
  }

  throw new Error(`Failed to fetch. Tried: ${candidates.join(", ")}`);
};

const requestJson = async (path, options = {}, timeoutMs = 10000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await apiFetch(path, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    }, timeoutMs);

    const text = await response.text();
    let data = {};
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(text || "Server returned invalid JSON");
      }
    }

    if (!response.ok) {
      throw new Error(data.message || data.error || data.msg || "Request failed");
    }

    return data;
  } catch (error) {
    throw error;
  } finally {
    controller.abort();
    clearTimeout(timeoutId);
  }
};

// ==========================
// 🔐 SIGNUP
// ==========================
export const signup = async (userData) => {
  try {
    const data = await requestJson("/signup", {
      method: "POST",
      body: JSON.stringify(userData),
    });
    return { success: true, data };
  } catch (error) {
    console.error("Signup error:", error);
    return { success: false, error: error.message || "Network error. Please try again." };
  }
};


// ==========================
// 🔐 LOGIN
// ==========================
export const login = async (credentials) => {
  try {
    const data = await requestJson("/login", {
      method: "POST",
      body: JSON.stringify(credentials),
    });

    if (data.token) localStorage.setItem("token", data.token);
    if (data.user) {
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("loggedInUser", JSON.stringify({
        id: data.user.id || data.user._id,
        _id: data.user._id || data.user.id,
        username: data.user.username,
        fullname: data.user.fullname,
        email: data.user.email,
      }));
    }
    if (data.token && data.user) {
      await syncUserLearningData(data.token);
    }
    return { success: true, data };
  } catch (error) {
    console.error("Login fetch error:", error);
    return { success: false, error: error.message || "Server error" };
  }
};


// ==========================
// 🤖 AI LEARNING API
// ==========================
export const getAIContent = async (topic) => {
  try {
    const response = await fetch(`${API_URL}/ai-learning`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ topic }),
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true, data };
    } else {
      return { success: false, error: data.msg || "AI request failed" };
    }
  } catch (error) {
    console.error("AI error:", error);
    return { success: false, error: "Network error" };
  }
};


// ==========================
// 🔒 AUTH CHECK
// ==========================
export const isAuthenticated = () => {
  return !!localStorage.getItem("token");
};

export const getLoggedInUser = () => {
  const rawUser = localStorage.getItem('loggedInUser') || localStorage.getItem('user');
  if (!rawUser) return null;
  try {
    const parsed = JSON.parse(rawUser);
    return {
      _id: parsed._id || parsed.id || parsed.userId || '',
      fullname: parsed.fullname || parsed.name || parsed.username || '',
      email: parsed.email || '',
      username: parsed.username || parsed.email || ''
    };
  } catch {
    return null;
  }
};

const getUserStorageKey = (key) => {
  const user = getLoggedInUser();
  return `${key}_${getCurrentUserId(user)}`;
};

const getCurrentUserId = (user = getLoggedInUser()) => user?._id || user?.email || user?.username || 'guest';

const getUserScopedProgressKey = (key) => `${key}-${getCurrentUserId()}`;

export const getEnrolledCourses = () => {
  const raw = localStorage.getItem(getUserStorageKey('enrolledCourses'));
  if (!raw) return [];
  try {
    return JSON.parse(raw) || [];
  } catch {
    return [];
  }
};

export const getEnrollmentPreferences = () => {
  const raw = localStorage.getItem(getUserStorageKey('enrollmentPreferences'));
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const getCourseType = (slug) => {
  if (!slug) return 'beginner';
  return getEnrollmentPreferences()[slug]?.courseType || 'beginner';
};

export const isCourseEnrolled = (slug) => {
  if (!slug) return false;
  return getEnrolledCourses().includes(slug);
};

export const addEnrolledCourse = async (slug, courseType = 'beginner', courseName = '') => {
  if (!slug) return false;
  const enrolled = getEnrolledCourses();
  if (!enrolled.includes(slug)) {
    enrolled.push(slug);
    localStorage.setItem(getUserStorageKey('enrolledCourses'), JSON.stringify(enrolled));
  }
  const preferences = getEnrollmentPreferences();
  preferences[slug] = {
    ...(preferences[slug] || {}),
    courseType,
    courseName: courseName || preferences[slug]?.courseName || slug,
    enrolledAt: preferences[slug]?.enrolledAt || new Date().toISOString(),
  };
  localStorage.setItem(getUserStorageKey('enrollmentPreferences'), JSON.stringify(preferences));

  const token = localStorage.getItem('token');
  if (token) {
    await requestJson('/user/enroll', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        courseSlug: slug,
        courseName: courseName || slug,
        courseType,
      }),
    });
  }

  return true;
};


// ==========================
// 🚪 LOGOUT
// ==========================
export const saveCourseProgress = async ({ courseSlug, level, stageKey, progressData }) => {
  const token = localStorage.getItem('token');
  if (!token) return { success: false, error: 'Authentication required' };

  return requestJson('/user/progress', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ courseSlug, level, stageKey, progressData }),
  });
};

export const saveFinalAssessment = async ({ courseSlug, courseName, level, score, passed }) => {
  const token = localStorage.getItem('token');
  if (!token) return { success: false, error: 'Authentication required' };

  return requestJson('/user/final-assessment', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ courseSlug, courseName, level, score, passed }),
  });
};

export const saveCertificate = async ({ courseSlug, courseName, level, score }) => {
  const token = localStorage.getItem('token');
  if (!token) return { success: false, error: 'Authentication required' };

  return requestJson('/user/certificate', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ courseSlug, courseName, level, score }),
  });
};

export const syncUserLearningData = async (providedToken) => {
  const token = providedToken || localStorage.getItem('token');
  if (!token) return { success: false, data: { enrollments: [], progress: [], certificates: [] } };

  const response = await requestJson('/user/sync', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = response.data || {};
  const enrollments = data.enrollments || [];
  const preferences = {};

  localStorage.setItem(
    getUserStorageKey('enrolledCourses'),
    JSON.stringify(enrollments.map((enrollment) => enrollment.courseSlug))
  );

  enrollments.forEach((enrollment) => {
    preferences[enrollment.courseSlug] = {
      courseType: enrollment.courseType || 'beginner',
      courseName: enrollment.courseName || enrollment.courseSlug,
      enrolledAt: enrollment.enrolledAt,
    };
  });

  localStorage.setItem(getUserStorageKey('enrollmentPreferences'), JSON.stringify(preferences));

  (data.progress || []).forEach((item) => {
    const stageProgress = item.stageProgress || {};
    const normalizedProgress = stageProgress instanceof Map ? Object.fromEntries(stageProgress) : stageProgress;
    localStorage.setItem(
      getUserScopedProgressKey(`roadmap-stage-progress-${item.courseSlug}-${item.level}`),
      JSON.stringify(normalizedProgress || {})
    );

    if (item.finalAssessment) {
      localStorage.setItem(
        getUserScopedProgressKey(`roadmap-final-assessment-progress-${item.courseSlug}`),
        JSON.stringify({ 'final-assessment': item.finalAssessment })
      );
    }
  });

  return response;
};

export const logout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("loggedInUser");
  window.dispatchEvent(new Event("auth-changed"));
};

// ==========================
// 🤖 ORIGINAL sendAIRequest FUNCTION (PRESERVED)
// ==========================
export const sendAIRequest = async (text, file) => {
  const formData = new FormData();

  if (text) formData.append("text", text);
  if (file) formData.append("image", file);

  const res = await fetch(`${API_URL}/ai`, {
    method: "POST",
    body: formData,
  });

  return res.json();
};

// ==========================
// 🖼️ NEW IMAGE PROCESSING FUNCTIONS (ADDED BELOW)
// ==========================

// NEW: Analyze image and get description/caption using Hugging Face
export const analyzeImage = async (imageFile) => {
  const formData = new FormData();
  formData.append("image", imageFile);

  try {
    const response = await fetch(`${API_URL}/analyze-image`, {
      method: "POST",
      body: formData,
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Image analysis error:", error);
    return { success: false, error: error.message };
  }
};

// NEW: Classify image content using Hugging Face
export const classifyImage = async (imageFile) => {
  const formData = new FormData();
  formData.append("image", imageFile);

  try {
    const response = await fetch(`${API_URL}/classify-image`, {
      method: "POST",
      body: formData,
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Image classification error:", error);
    return { success: false, error: error.message };
  }
};

// NEW: Detect objects in image using Hugging Face
export const detectObjectsInImage = async (imageFile) => {
  const formData = new FormData();
  formData.append("image", imageFile);

  try {
    const response = await fetch(`${API_URL}/detect-objects`, {
      method: "POST",
      body: formData,
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Object detection error:", error);
    return { success: false, error: error.message };
  }
};

// NEW: Generate content with optional image support
export const generateContentWithImage = async (topic, imageFile = null) => {
  const formData = new FormData();
  formData.append("topic", topic);
  if (imageFile) {
    formData.append("image", imageFile);
  }

  try {
    const response = await fetch(`${API_URL}/generate-content-with-image`, {
      method: "POST",
      body: formData,
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Generate content with image error:", error);
    return { success: false, error: error.message };
  }
};

// NEW: Helper function to convert file to base64 (useful for some API configurations)
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

// NEW: Send both text and image to Hugging Face for combined analysis
export const sendCombinedAIRequest = async (text, imageFile) => {
  const formData = new FormData();
  
  if (text) formData.append("text", text);
  if (imageFile) formData.append("image", imageFile);

  try {
    const res = await fetch(`${API_URL}/ai-combined`, {
      method: "POST",
      body: formData,
    });
    
    return await res.json();
  } catch (error) {
    console.error("Combined AI request error:", error);
    return { success: false, error: error.message };
  }
};

// NEW: Get image caption only (simplified version)
export const getImageCaption = async (imageFile) => {
  const formData = new FormData();
  formData.append("image", imageFile);

  try {
    const response = await fetch(`${API_URL}/image-caption`, {
      method: "POST",
      body: formData,
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Image caption error:", error);
    return { success: false, error: error.message };
  }
};

// NEW: Process multiple images at once
export const processMultipleImages = async (imageFiles) => {
  const formData = new FormData();
  
  imageFiles.forEach((file, index) => {
    formData.append(`image_${index}`, file);
  });

  try {
    const response = await fetch(`${API_URL}/process-images`, {
      method: "POST",
      body: formData,
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Multiple images processing error:", error);
    return { success: false, error: error.message };
  }
};

// NEW: Check if Hugging Face model is ready
export const checkModelStatus = async () => {
  try {
    const response = await fetch(`${API_URL}/model-status`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Model status check error:", error);
    return { success: false, error: error.message };
  }
};

// ==========================
// 📝 NOTE2QUIZ FUNCTIONS (ADD THIS ENTIRE SECTION AT THE END)
// ==========================

// Generate quiz from text notes
export const generateQuizFromNotes = async (notes, difficulty = "medium") => {
  try {
    const response = await fetch(`${API_URL}/generate-quiz`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ 
        notes, 
        difficulty 
      }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Quiz generation error:", error);
    return { success: false, error: error.message };
  }
};

// Alternative function name that matches your frontend
export const generateQuiz = async (notes, difficulty) => {
  return generateQuizFromNotes(notes, difficulty);
};



const API = axios.create({ baseURL: getActiveApiUrl() });

API.interceptors.request.use((req) => {
  req.baseURL = getActiveApiUrl();
  const token = localStorage.getItem('token');
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

export const searchRoadmap = (query) => API.post('/roadmap/search', { query });
export const completeStep = (courseName, stepIndex) => API.post('/roadmap/complete-step', { courseName, stepIndex });
export const getProgress = (courseName) => API.get(`/roadmap/progress/${courseName}`);
export const submitQuiz = (courseName, stepIndex, answers) => API.post('/roadmap/submit-quiz', { courseName, stepIndex, answers });
export const downloadNotes = (courseName, stepIndex) => API.get(`/roadmap/download-notes/${courseName}/${stepIndex}`, { responseType: 'blob' });
export const getCertificate = (courseName) => API.get(`/roadmap/certificate/${courseName}`, { responseType: 'blob' });